import React, { useState, useMemo, useCallback } from 'react';

import { Link } from '@backstage/core-components';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  makeStyles,
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import DeleteIcon from '@material-ui/icons/Delete';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import InfoIcon from '@material-ui/icons/Info';
import RestoreIcon from '@material-ui/icons/Restore';
import WarningIcon from '@material-ui/icons/Warning';
import { Alert } from '@material-ui/lab';

import { EntitySelector } from './EntitySelector';
import { TeamSelector } from './TeamSelector';
import { getHandlerForKind, hasHandlerForKind } from './config';
import { useGenericOwnershipHandler } from './handlers/genericHandler';
import { useJiraTeamOwnershipHandler } from './handlers/jiraTeamHandler';
import { usePagerDutyTeamOwnershipHandler } from './handlers/pagerDutyTeamHandler';
import { useRepositoryOwnershipHandler } from './handlers/repositoryHandler';
import { useSubscriptionOwnershipHandler } from './handlers/subscriptionHandler';
import {
  ManageOwnershipDialogProps,
  OwnershipEntity,
  OwnershipHandlerResult,
} from './types';

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 8,
      minWidth: 1100,
      maxWidth: 1200,
    },
  },
  title: {
    padding: theme.spacing(3, 3, 1),
    '& .MuiTypography-root': {
      fontSize: 20,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
  },
  content: {
    padding: theme.spacing(2, 3, 2),
    overflowY: 'auto',
    maxHeight: '50vh',
  },
  addSectionPanel: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  description: {
    fontSize: 14,
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    maxHeight: 400,
  },
  table: {
    '& .MuiTableCell-head': {
      backgroundColor: theme.palette.background.default,
      fontWeight: 600,
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: theme.palette.text.secondary,
      padding: theme.spacing(1.5, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiTableCell-body': {
      padding: theme.spacing(1, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: 'middle',
    },
    '& .MuiTableRow-root:last-child .MuiTableCell-body': {
      borderBottom: 'none',
    },
  },
  entityNameCell: {
    width: '30%',
    minWidth: 180,
  },
  teamCell: {
    width: '50%',
    minWidth: 300,
  },
  actionsCell: {
    width: '20%',
    minWidth: 100,
    textAlign: 'right',
  },
  entityLink: {
    fontFamily: 'monospace',
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  entityName: {
    fontFamily: 'monospace',
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  teamAutocomplete: {
    '& .MuiFormControl-root': {
      marginTop: 0,
      marginBottom: 0,
    },
    '& .MuiInputBase-root': {
      fontSize: 13,
    },
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
  },
  iconButton: {
    padding: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  markUnknownButton: {
    '&:hover': {
      color: theme.palette.warning.main,
      backgroundColor: 'rgba(255, 152, 0, 0.08)',
    },
  },
  deleteButton: {
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: 'rgba(244, 67, 54, 0.08)',
    },
  },
  actions: {
    padding: theme.spacing(2, 3, 3),
    gap: theme.spacing(1),
  },
  cancelButton: {
    textTransform: 'none',
    fontWeight: 500,
    padding: theme.spacing(1, 2.5),
  },
  confirmButton: {
    textTransform: 'none',
    fontWeight: 500,
    padding: theme.spacing(1, 2.5),
    minWidth: 140,
  },
  proposedSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 6,
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
  },
  proposedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  proposedLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  proposedItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1.5),
    marginBottom: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    '&:last-child': { marginBottom: 0 },
  },
  proposedItemName: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  claimButton: {
    textTransform: 'none',
    fontSize: 12,
    minWidth: 80,
  },
  confirmedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  confirmedLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  addSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: theme.palette.background.default,
  },
  addSectionHelperText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    marginBottom: theme.spacing(1.5),
  },
  addSectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pendingRemovalSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: 6,
    backgroundColor: `${theme.palette.warning.light}20`,
  },
  pendingRemovalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  pendingRemovalLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.warning.dark,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pendingRemovalItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  pendingRemovalName: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  restoreButton: {
    textTransform: 'none',
    fontSize: 12,
  },
}));

interface EntityOwnerState {
  [entityId: string]: string | undefined;
}

/**
 * Extract team name from various formats:
 * - "group:default/my-team" -> "my-team"
 * - "my-team" -> "my-team"
 * - undefined -> undefined
 */
function extractTeamName(ownerRef: string | undefined): string | undefined {
  if (!ownerRef) return undefined;
  if (ownerRef.includes('/')) {
    return ownerRef.split('/').pop();
  }
  if (ownerRef.includes(':')) {
    return ownerRef.split(':').pop();
  }
  return ownerRef;
}

/**
 * Internal component that uses the handler hook.
 */
const ManageOwnershipDialogContent = ({
  entities: initialEntities,
  entityLabel,
  entityKind,
  title,
  description,
  submitButtonText,
  onClose,
  onSuccess,
  loading: externalLoading,
  showMarkAsUnknown,
  disableTeamSelection,
  onRemoveEntity,
  allowAdd,
  deleteMode = 'none',
  defaultOwner,
  proposedEntities: initialProposed,
  entitySelectFilterFn,
}: {
  entities: OwnershipEntity[];
  entityLabel: string;
  entityKind?: string;
  title?: string;
  description?: string;
  submitButtonText?: string;
  onClose: () => void;
  onSuccess?: () => void;
  loading?: boolean;
  showMarkAsUnknown?: boolean;
  disableTeamSelection?: boolean;
  onRemoveEntity?: (entity: OwnershipEntity) => void;
  allowAdd?: boolean;
  deleteMode?: 'none' | 'remove' | 'unassign';
  defaultOwner?: string;
  proposedEntities?: OwnershipEntity[];
  entitySelectFilterFn?: (
    annotations: Record<string, string> | undefined,
  ) => boolean;
}) => {
  const classes = useStyles();

  // Track entities internally for deletion
  const [entities, setEntities] = useState<OwnershipEntity[]>(initialEntities);

  // Track entities pending removal (for 'unassign' mode)
  const [pendingRemovals, setPendingRemovals] = useState<OwnershipEntity[]>([]);

  // Proposed section state
  const [localProposed, setLocalProposed] = useState<OwnershipEntity[]>(
    initialProposed ?? [],
  );
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    entity: OwnershipEntity;
    mode: 'claim' | 'add';
  } | null>(null);

  // Store the initial entity kind - use entityKind prop as fallback for empty lists
  const [initialKind] = useState(
    () => initialEntities[0]?.kind || entityKind || '',
  );

  // Track owner selection per entity - extract team name for autocomplete
  const [ownersByEntity, setOwnersByEntity] = useState<EntityOwnerState>(() => {
    const initial: EntityOwnerState = {};
    initialEntities.forEach(entity => {
      const owner = entity.proposedOwner || entity.currentOwner;
      initial[entity.id] = extractTeamName(owner);
    });
    return initial;
  });

  // Get the handler for this entity kind - use the initial kind (stable)
  const handlerConfig = useMemo(
    () => (initialKind ? getHandlerForKind(initialKind) : null),
    [initialKind],
  );

  // Call all handlers unconditionally (React hooks rule) and select the right one
  const repositoryHandler = useRepositoryOwnershipHandler();
  const genericHandler = useGenericOwnershipHandler();
  const jiraTeamHandler = useJiraTeamOwnershipHandler();
  const subscriptionHandler = useSubscriptionOwnershipHandler();
  const pagerDutyTeamHandler = usePagerDutyTeamOwnershipHandler();

  // Select the appropriate handler based on config
  let activeHandler: OwnershipHandlerResult = genericHandler;
  if (handlerConfig?.handler === useRepositoryOwnershipHandler) {
    activeHandler = repositoryHandler;
  } else if (handlerConfig?.handler === useJiraTeamOwnershipHandler) {
    activeHandler = jiraTeamHandler;
  } else if (handlerConfig?.handler === useSubscriptionOwnershipHandler) {
    activeHandler = subscriptionHandler;
  } else if (handlerConfig?.handler === usePagerDutyTeamOwnershipHandler) {
    activeHandler = pagerDutyTeamHandler;
  }
  const { submit, loading: handlerLoading } = handlerConfig
    ? activeHandler
    : { submit: async () => true, loading: false };

  const loading = externalLoading || handlerLoading;

  const markEntitiesAsUnknown = useCallback(
    async (targetEntities: OwnershipEntity[]) => {
      if (handlerConfig?.handler === useRepositoryOwnershipHandler) {
        return repositoryHandler.markAsUnknown(targetEntities);
      }
      return genericHandler.markAsUnknown(targetEntities);
    },
    [genericHandler, handlerConfig, repositoryHandler],
  );

  const handleOwnerChange = useCallback(
    (entityId: string, ownerRef: string | undefined) => {
      setOwnersByEntity(prev => ({
        ...prev,
        [entityId]: ownerRef,
      }));
    },
    [],
  );

  const handleActionConfirm = useCallback(async () => {
    if (!pendingAction) return;
    const { entity, mode } = pendingAction;
    setPendingAction(null);

    if (mode === 'add') {
      setEntities(prev => [...prev, entity]);
      setOwnersByEntity(prev => ({
        ...prev,
        [entity.id]: extractTeamName(defaultOwner),
      }));
      return;
    }

    // mode === 'claim'
    const teamName = extractTeamName(defaultOwner);
    if (!teamName) return;

    setClaimingId(entity.id);
    const success = await submit([entity], teamName);
    setClaimingId(null);

    if (success) {
      setLocalProposed(prev => prev.filter(e => e.id !== entity.id));
      setEntities(prev => [...prev, entity]);
      setOwnersByEntity(prev => ({ ...prev, [entity.id]: teamName }));
    }
  }, [defaultOwner, pendingAction, submit]);

  const handleRemoveEntity = useCallback(
    (entity: OwnershipEntity) => {
      setEntities(prev => prev.filter(e => e.id !== entity.id));
      setOwnersByEntity(prev => {
        const next = { ...prev };
        delete next[entity.id];
        return next;
      });

      // In 'unassign' mode, track for pending removal
      if (deleteMode === 'unassign') {
        setPendingRemovals(prev => [...prev, entity]);
      }

      onRemoveEntity?.(entity);
    },
    [onRemoveEntity, deleteMode],
  );

  const handleRestoreEntity = useCallback((entity: OwnershipEntity) => {
    // Move from pending removals back to entities
    setPendingRemovals(prev => prev.filter(e => e.id !== entity.id));
    setEntities(prev => [...prev, entity]);
    // Restore the owner selection
    const owner = entity.proposedOwner || entity.currentOwner;
    setOwnersByEntity(prev => ({
      ...prev,
      [entity.id]: extractTeamName(owner),
    }));
  }, []);

  const handleAddEntity = useCallback((entity: OwnershipEntity) => {
    setPendingAction({ entity, mode: 'add' });
  }, []);

  // Get set of entity IDs for exclusion in add selector (include pending removals)
  const entityIds = useMemo(() => {
    const ids = new Set(entities.map(e => e.id));
    pendingRemovals.forEach(e => ids.add(e.id));
    return ids;
  }, [entities, pendingRemovals]);

  const handleMarkAsUnknown = useCallback(
    async (entity: OwnershipEntity) => {
      const success = await markEntitiesAsUnknown([entity]);
      if (success) {
        // Remove from list after marking as unknown
        handleRemoveEntity(entity);
      }
    },
    [handleRemoveEntity, markEntitiesAsUnknown],
  );

  const handleSubmit = async () => {
    // Group entities by their selected owner
    const byOwner = new Map<string, OwnershipEntity[]>();

    for (const entity of entities) {
      const owner = ownersByEntity[entity.id];
      if (!owner) continue;

      const group = byOwner.get(owner) || [];
      group.push(entity);
      byOwner.set(owner, group);
    }

    // Submit each group
    let allSuccess = true;
    for (const [owner, group] of byOwner) {
      const success = await submit(group, owner);
      if (!success) allSuccess = false;
    }

    // Handle pending removals (unassign mode)
    if (deleteMode === 'unassign' && pendingRemovals.length > 0) {
      const unassignSuccess = await markEntitiesAsUnknown(pendingRemovals);
      if (!unassignSuccess) allSuccess = false;
    }

    if (allSuccess) {
      onClose();
      onSuccess?.();
    }
  };

  const getButtonText = () => {
    if (loading) return 'Updating...';
    if (submitButtonText) return submitButtonText;

    const updateCount = entities.filter(e => ownersByEntity[e.id]).length;
    const removeCount = pendingRemovals.length;

    if (updateCount > 0 && removeCount > 0) {
      return `Confirm ${updateCount} Update${
        updateCount > 1 ? 's' : ''
      } + ${removeCount} Removal${removeCount > 1 ? 's' : ''}`;
    }
    if (removeCount > 0) {
      return `Confirm ${removeCount} Removal${removeCount > 1 ? 's' : ''}`;
    }
    if (updateCount > 1) {
      return `Confirm ${updateCount} Ownerships`;
    }
    return 'Confirm Ownership';
  };

  const getTitle = () => {
    if (title) return title;
    return `Manage ${entityLabel} Ownership`;
  };

  const getDescription = () => {
    if (description) return description;
    const count = entities.length;
    const entityWord =
      count === 1 ? entityLabel.toLowerCase() : `${entityLabel.toLowerCase()}s`;
    return `Assign team ownership for ${
      count === 1 ? 'this' : `these ${count}`
    } ${entityWord}.`;
  };

  // Get display name for team - only return actual display names, not extracted names
  // If no display name is available, return undefined so TeamSelector will fetch it
  const getTeamDisplayName = (entity: OwnershipEntity): string | undefined => {
    return entity.proposedOwnerDisplayName || entity.currentOwnerDisplayName;
  };

  const isSubmitDisabled = () => {
    if (loading) return true;

    // Allow submit if there are pending removals
    if (pendingRemovals.length > 0) return false;

    // Otherwise, need at least one entity with an owner selected
    if (entities.length === 0) return true;
    return !entities.some(entity => ownersByEntity[entity.id]);
  };

  return (
    <>
      <DialogTitle className={classes.title}>{getTitle()}</DialogTitle>

      <DialogContent className={classes.content}>
        <Typography className={classes.description}>
          {getDescription()}
        </Typography>

        {localProposed.length > 0 && (
          <Box className={classes.proposedSection}>
            <Box className={classes.proposedHeader}>
              <InfoIcon
                style={{ fontSize: 16, color: 'inherit' }}
                color="primary"
              />
              <Typography className={classes.proposedLabel}>
                Proposed for Your Team ({localProposed.length})
              </Typography>
            </Box>
            {localProposed.map(entity => (
              <Box key={entity.id} className={classes.proposedItem}>
                {entity.entityLink ? (
                  <Link
                    to={entity.entityLink}
                    className={classes.proposedItemName}
                  >
                    {entity.displayName || entity.name}
                  </Link>
                ) : (
                  <span className={classes.proposedItemName}>
                    {entity.displayName || entity.name}
                  </span>
                )}
                <Button
                  className={classes.claimButton}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => setPendingAction({ entity, mode: 'claim' })}
                  disabled={loading || claimingId !== null}
                  startIcon={
                    claimingId === entity.id ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <AssignmentTurnedInIcon fontSize="small" />
                    )
                  }
                >
                  {claimingId === entity.id ? 'Claiming...' : 'Claim'}
                </Button>
              </Box>
            ))}
          </Box>
        )}

        {initialProposed !== undefined && (
          <Box className={classes.confirmedHeader}>
            <Typography className={classes.confirmedLabel}>
              Confirmed Repositories ({entities.length})
            </Typography>
          </Box>
        )}

        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.entityNameCell}>
                  {entityLabel}
                </TableCell>
                <TableCell className={classes.teamCell}>Team</TableCell>
                {(deleteMode !== 'none' || showMarkAsUnknown) && (
                  <TableCell className={classes.actionsCell}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {entities.map(entity => {
                const initialTeamName = getTeamDisplayName(entity);
                return (
                  <TableRow key={entity.id}>
                    <TableCell className={classes.entityNameCell}>
                      {entity.entityLink ? (
                        <Link
                          to={entity.entityLink}
                          className={classes.entityLink}
                        >
                          {entity.displayName || entity.name}
                        </Link>
                      ) : (
                        <span className={classes.entityName}>
                          {entity.displayName || entity.name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={classes.teamCell}>
                      <Box className={classes.teamAutocomplete}>
                        <TeamSelector
                          value={ownersByEntity[entity.id]}
                          displayName={initialTeamName}
                          onChange={value =>
                            handleOwnerChange(entity.id, value)
                          }
                          disabled={loading || disableTeamSelection}
                        />
                      </Box>
                    </TableCell>
                    {(deleteMode !== 'none' || showMarkAsUnknown) && (
                      <TableCell className={classes.actionsCell}>
                        <Box className={classes.actionButtons}>
                          {showMarkAsUnknown && (
                            <Tooltip title="Mark as Unknown">
                              <IconButton
                                className={`${classes.iconButton} ${classes.markUnknownButton}`}
                                size="small"
                                onClick={() => handleMarkAsUnknown(entity)}
                                disabled={loading}
                              >
                                <HelpOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {deleteMode !== 'none' && (
                            <Tooltip
                              title={
                                deleteMode === 'unassign'
                                  ? 'Remove from team'
                                  : 'Remove from list'
                              }
                            >
                              <IconButton
                                className={`${classes.iconButton} ${classes.deleteButton}`}
                                size="small"
                                onClick={() => handleRemoveEntity(entity)}
                                disabled={loading}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pending Removals Section - only in unassign mode */}
        {deleteMode === 'unassign' && pendingRemovals.length > 0 && (
          <Box className={classes.pendingRemovalSection}>
            <Box className={classes.pendingRemovalHeader}>
              <WarningIcon
                color="inherit"
                style={{ fontSize: 18, color: '#ed6c02' }}
              />
              <Typography className={classes.pendingRemovalLabel}>
                Pending Removal ({pendingRemovals.length}) - will be unassigned
                from team
              </Typography>
            </Box>
            {pendingRemovals.map(entity => (
              <Box key={entity.id} className={classes.pendingRemovalItem}>
                <Typography className={classes.pendingRemovalName}>
                  {entity.displayName || entity.name}
                </Typography>
                <Button
                  className={classes.restoreButton}
                  size="small"
                  startIcon={<RestoreIcon fontSize="small" />}
                  onClick={() => handleRestoreEntity(entity)}
                  disabled={loading}
                >
                  Restore
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {allowAdd && initialKind && (
        <Box className={classes.addSectionPanel}>
          <Typography className={classes.addSectionLabel}>
            Add {entityLabel}
          </Typography>
          <Typography className={classes.addSectionHelperText}>
            Only unconfirmed repositories are searchable here. To claim one
            already owned by another team, ask them to remove it first.
          </Typography>
          <EntitySelector
            kind={initialKind}
            excludeIds={entityIds}
            onSelect={handleAddEntity}
            disabled={loading}
            filterFn={option => {
              if (
                option.annotations?.['tomtom.com/ownership-status'] ===
                'confirmed'
              ) {
                return false;
              }
              return (
                !entitySelectFilterFn ||
                entitySelectFilterFn(option.annotations)
              );
            }}
          />
        </Box>
      )}

      <DialogActions className={classes.actions}>
        <Button
          className={classes.cancelButton}
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          className={classes.confirmButton}
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitDisabled()}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {getButtonText()}
        </Button>
      </DialogActions>

      {/* Add / Claim confirmation dialog */}
      <Dialog
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Claim Repository Ownership</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 14, lineHeight: 1.7 }}>
            By claiming{' '}
            <strong>
              {pendingAction?.entity.displayName || pendingAction?.entity.name}
            </strong>
            , your team will be set as the <strong>business owner</strong> of
            this repository.
          </Typography>
          <Typography style={{ fontSize: 14, lineHeight: 1.7, marginTop: 12 }}>
            This has <strong>no impact on access management</strong>. The
            repository will be attributed to your team for metrics and reporting
            purposes.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button
            onClick={() => setPendingAction(null)}
            style={{ textTransform: 'none' }}
          >
            No, cancel
          </Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            color="primary"
            style={{ textTransform: 'none' }}
          >
            Yes, claim ownership
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * A reusable dialog component for managing entity ownership.
 *
 * Each row displays:
 * - Entity name (linkable to catalog page)
 * - Team autocomplete
 * - Action buttons: Mark as Unknown (optional), Delete
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ManageOwnershipDialog
 *   open={dialogOpen}
 *   onClose={() => setDialogOpen(false)}
 *   entities={[entityToOwnershipEntity(entity)]}
 *   onSuccess={() => window.location.reload()}
 * />
 *
 * // With Mark as Unknown per row (for action-required page)
 * <ManageOwnershipDialog
 *   open={dialogOpen}
 *   onClose={() => setDialogOpen(false)}
 *   entities={repos.map(proposedRepoToOwnershipEntity)}
 *   showMarkAsUnknown
 *   onSuccess={() => refetch()}
 * />
 * ```
 */
export const ManageOwnershipDialog = ({
  open,
  onClose,
  entities,
  onSuccess,
  title,
  description,
  submitButtonText,
  showMarkAsUnknown,
  disableTeamSelection,
  onRemoveEntity,
  allowAdd,
  entityKind,
  deleteMode = 'none',
  defaultOwner,
  proposedEntities,
  entitySelectFilterFn,
}: ManageOwnershipDialogProps) => {
  const classes = useStyles();

  // Determine the effective entity kind
  const effectiveKind = entities?.[0]?.kind || entityKind;

  // Validate entities
  const validationError = useMemo(() => {
    // Allow empty entities if allowAdd is true and entityKind is provided
    if (!entities || entities.length === 0) {
      if (allowAdd && entityKind && hasHandlerForKind(entityKind)) {
        return null; // Valid - can start with empty list
      }
      return 'No entities provided';
    }

    const firstKind = entities[0].kind;
    const hasMixedKinds = entities.some(e => e.kind !== firstKind);
    if (hasMixedKinds) {
      return 'All entities must be of the same kind';
    }

    if (!hasHandlerForKind(firstKind)) {
      return `No ownership handler configured for entity kind: "${firstKind}"`;
    }

    return null;
  }, [entities, allowAdd, entityKind]);

  const entityLabel = useMemo(() => {
    if (effectiveKind) {
      try {
        return getHandlerForKind(effectiveKind).entityLabel;
      } catch {
        return effectiveKind;
      }
    }
    return 'Entity';
  }, [effectiveKind]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      className={classes.dialog}
    >
      {validationError ? (
        <>
          <DialogTitle className={classes.title}>Manage Ownership</DialogTitle>
          <DialogContent className={classes.content}>
            <Alert severity="error">{validationError}</Alert>
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        </>
      ) : (
        <ManageOwnershipDialogContent
          entities={entities}
          entityLabel={entityLabel}
          entityKind={effectiveKind}
          title={title}
          description={description}
          submitButtonText={submitButtonText}
          onClose={handleClose}
          onSuccess={onSuccess}
          showMarkAsUnknown={showMarkAsUnknown}
          disableTeamSelection={disableTeamSelection}
          onRemoveEntity={onRemoveEntity}
          allowAdd={allowAdd}
          deleteMode={deleteMode}
          defaultOwner={defaultOwner}
          proposedEntities={proposedEntities}
          entitySelectFilterFn={entitySelectFilterFn}
        />
      )}
    </Dialog>
  );
};
